import { TCustomerInsights } from '../i18n/TModules';
import {
  DEFAULT_FILTER_SELECTIONS,
  FILTER_CATEGORIES,
  decodeFilterValue
} from '../registry/filters';
import { IntelligenceFilterId } from '../types';
import { formatCustomRangeDetail } from './customRangeLabel';
import {
  ScreenFilterChip,
  ScreenFilterSignature,
  screenWireFiltersOf,
  Translate
} from './screenFilterSignature';
import { InsightFiltersValue } from './useInsightFilters';

/**
 * Turns the live Customer Insights filter state into a published signature, using the filter
 * panel's own copy so the chips and the conflict card's compare row read exactly as the bar does.
 *
 * The date range is always drawn: it applies to every time-based metric whatever the user picked.
 * Every other category is drawn only when it is off its default, so an untouched panel does not
 * fill the panel with six chips reading "All".
 *
 * The comparison window is not published at all: it is always the same length immediately before
 * the period (`useInsightFilters.ts` `previousRange`), so it adds nothing the Period chip does not
 * already say. The chip mirrors the date filter alone (user, 2026-09-14).
 */
export const insightFilterSignatureOf = (
  value: InsightFiltersValue,
  t: Translate
): ScreenFilterSignature => {
  const categoryLabel = (id: IntelligenceFilterId): string =>
    t(`${TCustomerInsights}.Filters.Categories.${id}.Title`);

  const optionLabel = (id: IntelligenceFilterId): string => {
    const { optionId, subValue } = decodeFilterValue(value.selections[id]);
    const label = t(`${TCustomerInsights}.Filters.Categories.${id}.Options.${optionId}`);

    if (subValue === undefined) {
      return label;
    }

    // The bar's own composite wording, so `Custom range · 08.07.2026 – 08.08.2026` and
    // `Age band · 25-34` read in the panel and in the chip the same way.
    return t(`${TCustomerInsights}.Filters.ChipValueWithDetail`, {
      option: label,
      detail: id === 'dateRange' ? formatCustomRangeDetail(subValue) : subValue
    });
  };

  const chips: ScreenFilterChip[] = [];

  FILTER_CATEGORIES.forEach(({ id }) => {
    if (id !== 'dateRange' && value.selections[id] === DEFAULT_FILTER_SELECTIONS[id]) {
      return;
    }

    // The date-range chip is published as `period`: Orbit draws it as the frame's `📅 Period:`
    // chip (`5032:12031`) rather than under the panel's own "Date Range" heading.
    chips.push({
      id: id === 'dateRange' ? 'period' : id,
      label: categoryLabel(id),
      value: optionLabel(id)
    });
  });

  return {
    // Every category, defaults included: the identity has to move when a filter returns to its
    // default as much as when it leaves it.
    key: FILTER_CATEGORIES.map(({ id }) => `${id}=${value.selections[id]}`).join('|'),
    chips,
    ...screenWireFiltersOf(value.selections, DEFAULT_FILTER_SELECTIONS)
  };
};

export default insightFilterSignatureOf;
